'use client';

import { ReactNode, useCallback } from 'react';
import classNames from 'classnames';

import { PropsWithClassname } from '@/app/types';

export interface CheckboxProps extends PropsWithClassname {
  isChecked: boolean;
  onChange: (nextIsChecked: boolean) => void;
  label: string | ReactNode;
  disabled?: boolean;
  isErrorVisible?: boolean;
  errorMessage?: ReactNode;
  note?: string;
  accent?: ReactNode;
}

function Checkbox({
  isChecked,
  onChange,
  label,
  disabled,
  isErrorVisible,
  note,
  accent,
  errorMessage,
  className,
}: CheckboxProps) {
  const handleChange = useCallback(() => {
    if (disabled) {
      return;
    }

    onChange(!isChecked);
  }, [onChange, disabled, isChecked]);

  return (
    <div
      onClick={handleChange}
      className={classNames(className, 'group pb-[16px]', {
        'cursor-pointer': !disabled,
        'cursor-not-allowed': disabled,
      })}
    >
      <div className="flex">
        <input
          type="checkbox"
          checked={isChecked}
          className={classNames('mt-[3px] ml-[2px] mr-[6px] w-[20px] h-[16px] shrink-0', {
            'accent-warning group-hover:accent-warning has-error': isErrorVisible,
            'accent-primary-mid group-hover:accent-primary-dark': !isErrorVisible,
          })}
          disabled={disabled}
          onChange={handleChange}
        />
        <div>
          <div className="flex flex-row justify-between md:gap-[8px]">
            <span
              className={classNames({
                'text-warning': isErrorVisible,
              })}
            >
              {label}
            </span>
            {accent}
          </div>
          {!!errorMessage && isErrorVisible && <div className="text-warning font-medium mb-[8px]">{errorMessage}</div>}
          {!!note && <small className="block text-[12px] leading-[1.4] text-neutral-smallDescription">{note}</small>}
        </div>
      </div>
    </div>
  );
}

export default Checkbox;
